import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import { hasAccess } from '../utils/permissions';
import {
    PageShell, PageHeader, Surface, Row, RowText, RowTitle, RowMeta,
    StatusPill, Search, Toolbar, Empty, ActionButton, MetricCard,
    DetailGrid, DetailItem, Avatar, StatePanel,
} from '../components/ui';

/* ─── Colour mapping for community statuses ──────────────────────────────── */
const APPLICATION_STATUS_LABELS = {
    PENDING_REVIEW: 'Pending Review',
    MORE_INFO_REQUIRED: 'More Info Required',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
};

const LIFECYCLE_STATUS_LABELS = {
    ACTIVE: 'Active',
    SUSPENDED: 'Suspended',
    DEACTIVATED: 'Deactivated',
};

/* ─── StatusPill colour mapping extensions ────────────────────────────────
   StatusPill keys are lowercase. We map community-specific statuses. */
const pillLabel = (status) => {
    const map = { ...APPLICATION_STATUS_LABELS, ...LIFECYCLE_STATUS_LABELS };
    return map[status] || status;
};

/* ─── Tabs ────────────────────────────────────────────────────────────── */
const VIEWS = {
    LIST: 'list',
    APPLICATIONS: 'applications',
    REACTIVATION: 'reactivation',
    DETAIL: 'detail',
};

const TAB_STYLE = (active) => ({
    padding: '8px 16px',
    cursor: 'pointer',
    fontWeight: active ? 700 : 400,
    borderBottom: active ? '3px solid #7c3aed' : '3px solid transparent',
    color: active ? '#7c3aed' : '#566071',
    background: 'none',
    border: 'none',
    borderBottomWidth: 3,
    borderBottomStyle: 'solid',
    borderBottomColor: active ? '#7c3aed' : 'transparent',
    fontSize: 14,
});

const APP_TABS = ['ALL', 'PENDING_REVIEW', 'MORE_INFO_REQUIRED', 'APPROVED', 'REJECTED'];
const LIST_TABS = ['ALL', 'ACTIVE', 'SUSPENDED', 'DEACTIVATED'];

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function Communities() {
    const { permissions } = usePermissions();
    const adminRoles = (permissions && permissions.adminRoles) || [];

    /* ── gate ─────────────────────────────────────────────────────────── */
    if (!hasAccess('communities', adminRoles)) {
        return (
            <PageShell>
                <StatePanel state="error">You do not have permission to access Communities.</StatePanel>
            </PageShell>
        );
    }

    return <CommunitiesInner />;
}

function CommunitiesInner() {
    const [view, setView] = useState(VIEWS.LIST);
    const [communities, setCommunities] = useState([]);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [listTab, setListTab] = useState('ALL');
    const [appTab, setAppTab] = useState('PENDING_REVIEW');
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    /* ── moderation state ──────────────────────────────────────────── */
    const [actionNote, setActionNote] = useState('');
    const [deactivateReason, setDeactivateReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    /* ── data loading ──────────────────────────────────────────────── */
    const loadList = useCallback(() => {
        setLoading(true);
        httpClient(`${API_BASE_URL}/communities/admin/list?limit=500`)
            .then(({ json }) => {
                const docs = json.docs || json || [];
                setCommunities(docs);
            })
            .catch(err => console.error('Communities list fetch failed:', err))
            .finally(() => setLoading(false));
    }, []);

    const loadApplications = useCallback(() => {
        setLoading(true);
        httpClient(`${API_BASE_URL}/communities/admin/applications?limit=500`)
            .then(({ json }) => {
                const docs = json.docs || json || [];
                setApplications(docs);
            })
            .catch(err => console.error('Applications fetch failed:', err))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadList();
        loadApplications();
    }, [loadList, loadApplications]);

    /* ── detail loader ─────────────────────────────────────────────── */
    /* Uses the single-application endpoint to fetch one community directly by id,
       instead of searching the paginated list which could miss entries beyond page 1. */
    const openDetail = (community) => {
        setDetail(community);
        setDetailError(null);
        setView(VIEWS.DETAIL);
        setDetailLoading(true);
        httpClient(`${API_BASE_URL}/communities/admin/applications/${community._id}`)
            .then(({ json }) => {
                if (!json || !json._id) {
                    setDetailError('Community application not found. It may have been removed.');
                } else {
                    setDetail(json);
                    setDetailError(null);
                }
            })
            .catch(err => {
                console.error('Detail fetch failed:', err);
                const status = err.status || err.statusCode;
                if (status === 404) {
                    setDetailError('Community not found. It may have been deleted or the ID is invalid.');
                } else {
                    setDetailError(`Failed to load community details: ${err.message || 'Unknown error'}. The data shown may be incomplete.`);
                }
            })
            .finally(() => setDetailLoading(false));
    };

    /* ── application actions ───────────────────────────────────────── */
    const doApplicationAction = (id, action, body = {}) => {
        setActionLoading(true);
        httpClient(`${API_BASE_URL}/communities/admin/applications/${id}/${action}`, {
            method: 'POST',
            body: JSON.stringify(body),
        })
            .then(() => {
                loadApplications();
                loadList();
                setActionNote('');
                // Refresh detail if open — re-fetch for full data including reviewHistory
                if (detail && detail._id === id) {
                    httpClient(`${API_BASE_URL}/communities/admin/applications/${id}`)
                        .then(({ json }) => { if (json && json._id) setDetail(json); })
                        .catch(() => {
                            const updatedAppStatus = action === 'approve' ? 'APPROVED'
                                : action === 'reject' ? 'REJECTED'
                                : 'MORE_INFO_REQUIRED';
                            setDetail(prev => ({ ...prev, applicationStatus: updatedAppStatus }));
                        });
                }
            })
            .catch(err => {
                console.error(`Application ${action} failed:`, err);
                alert(`Action failed: ${err.message || 'Unknown error'}`);
            })
            .finally(() => setActionLoading(false));
    };

    /* ── moderation actions ────────────────────────────────────────── */
    const doModerationAction = (id, action, body = {}) => {
        setActionLoading(true);
        httpClient(`${API_BASE_URL}/communities/admin/${id}/${action}`, {
            method: 'POST',
            body: JSON.stringify(body),
        })
            .then(() => {
                loadList();
                loadApplications();
                setActionNote('');
                setDeactivateReason('');
                if (detail && detail._id === id) {
                    httpClient(`${API_BASE_URL}/communities/admin/applications/${id}`)
                        .then(({ json }) => { if (json && json._id) setDetail(json); })
                        .catch(() => {
                            // Fallback: update status optimistically
                            const updatedStatus = action === 'suspend' ? 'SUSPENDED'
                                : action === 'deactivate' ? 'DEACTIVATED'
                                : 'ACTIVE';
                            setDetail(prev => ({ ...prev, status: updatedStatus }));
                        });
                }
            })
            .catch(err => {
                console.error(`Moderation ${action} failed:`, err);
                alert(`Action failed: ${err.message || 'Unknown error'}`);
            })
            .finally(() => setActionLoading(false));
    };

    /* ── filtered lists ────────────────────────────────────────────── */
    const filteredCommunities = useMemo(() => {
        let list = communities;
        if (listTab !== 'ALL') list = list.filter(c => c.status === listTab);
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(c =>
                (c.name || '').toLowerCase().includes(q) ||
                (c.orgAdmin?.name || c.orgAdmin?.firstName || '').toLowerCase().includes(q) ||
                (c.city || '').toLowerCase().includes(q)
            );
        }
        return list;
    }, [communities, listTab, search]);

    const filteredApplications = useMemo(() => {
        let list = applications;
        if (appTab !== 'ALL') list = list.filter(c => c.applicationStatus === appTab);
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(c =>
                (c.name || '').toLowerCase().includes(q) ||
                (c.orgAdmin?.name || c.orgAdmin?.firstName || '').toLowerCase().includes(q)
            );
        }
        return list;
    }, [applications, appTab, search]);

    /* ── reactivation requests: deactivated communities with a pending request ── */
    const reactivationRequests = useMemo(() => {
        let list = communities.filter(c =>
            c.status === 'DEACTIVATED' && c.reactivationRequestedAt
        );
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(c =>
                (c.name || '').toLowerCase().includes(q) ||
                (c.orgAdmin?.name || c.orgAdmin?.firstName || '').toLowerCase().includes(q)
            );
        }
        return list;
    }, [communities, search]);

    /* ── counts for tabs ───────────────────────────────────────────── */
    const listCounts = useMemo(() => {
        const counts = { ALL: communities.length };
        LIST_TABS.forEach(t => { if (t !== 'ALL') counts[t] = communities.filter(c => c.status === t).length; });
        return counts;
    }, [communities]);

    const appCounts = useMemo(() => {
        const counts = { ALL: applications.length };
        APP_TABS.forEach(t => { if (t !== 'ALL') counts[t] = applications.filter(c => c.applicationStatus === t).length; });
        return counts;
    }, [applications]);

    /* ═══════════════════════════════ RENDER ═══════════════════════════════ */

    /* ── Detail view ───────────────────────────────────────────────── */
    if (view === VIEWS.DETAIL && detail) {
        const isAppPending = detail.applicationStatus === 'PENDING_REVIEW' || detail.applicationStatus === 'MORE_INFO_REQUIRED';

        return (
            <PageShell>
                <PageHeader
                    eyebrow="Communities"
                    title={detail.name || 'Community Detail'}
                    actions={
                        <ActionButton variant="secondary" onClick={() => { setDetail(null); setDetailError(null); setView(VIEWS.LIST); }}>
                            ← Back to list
                        </ActionButton>
                    }
                />

                {/* ── Error banner ── */}
                {detailError && (
                    <Surface>
                        <div style={{ padding: '16px', color: '#b3145a', fontSize: 14, fontWeight: 600 }}>
                            ⚠ {detailError}
                        </div>
                    </Surface>
                )}

                {/* ── Loading indicator ── */}
                {detailLoading && (
                    <Surface>
                        <StatePanel state="loading">Loading community details…</StatePanel>
                    </Surface>
                )}

                {/* ── Profile ── */}
                <Surface title="Community Profile">
                    <DetailGrid>
                        <DetailItem label="Name" value={detail.name} />
                        <DetailItem label="Organization Type" value={detail.organizationType} />
                        <DetailItem label="Country" value={detail.country} />
                        <DetailItem label="City" value={detail.city} />
                        <DetailItem label="Application Status">
                            <StatusPill status={(detail.applicationStatus || '').toLowerCase()}>
                                {pillLabel(detail.applicationStatus)}
                            </StatusPill>
                        </DetailItem>
                        {/* Finding 4: Only show lifecycle status after approval */}
                        {detail.applicationStatus === 'APPROVED' && (
                            <DetailItem label="Lifecycle Status">
                                <StatusPill status={(detail.status || '').toLowerCase()}>
                                    {pillLabel(detail.status)}
                                </StatusPill>
                            </DetailItem>
                        )}
                        <DetailItem label="Membership Mode" value={detail.membershipMode} />
                        <DetailItem label="Members" value={detail.activeMemberCount ?? '—'} />
                        <DetailItem label="Providers" value={detail.activeProviderCount ?? '—'} />
                        <DetailItem label="Short Description" value={detail.shortDescription} wide />
                        <DetailItem label="Full Description" value={detail.fullDescription || '—'} wide />
                        <DetailItem label="Website" value={detail.website || '—'} />
                        <DetailItem label="Contact Email" value={detail.contactEmail || '—'} />
                        <DetailItem label="Contact Phone" value={detail.contactPhone || '—'} />
                        <DetailItem label="Created" value={detail.createdAt ? new Date(detail.createdAt).toLocaleString() : '—'} />
                    </DetailGrid>
                </Surface>

                {/* ── Org Admin ── */}
                <Surface title="Organization Admin">
                    <DetailGrid>
                        <DetailItem label="Name" value={detail.orgAdmin?.name || detail.orgAdmin?.firstName || '—'} />
                        <DetailItem label="ID" value={detail.orgAdmin?._id || '—'} />
                    </DetailGrid>
                </Surface>

                {/* ── Verification Documents — renders all documents ── */}
                <Surface title="Verification Documents">
                    {/* Legacy hardcoded fields for older applications */}
                    {(!detail.documents || detail.documents.length === 0) && !detail.effectiveDocuments?.length && (
                        <DetailGrid>
                            <DetailItem label="Proof of Organization">
                                {detail.proofOfOrganization
                                    ? <a href={detail.proofOfOrganization} target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed' }}>View Document</a>
                                    : <span style={{ color: '#9a3d13', fontStyle: 'italic' }}>Not provided</span>}
                            </DetailItem>
                            <DetailItem label="Proof of Authority">
                                {detail.proofOfAuthority
                                    ? <a href={detail.proofOfAuthority} target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed' }}>View Document</a>
                                    : <span style={{ color: '#9a3d13', fontStyle: 'italic' }}>Not provided</span>}
                            </DetailItem>
                        </DetailGrid>
                    )}
                    {/* Full documents array — effectiveDocuments includes legacy-merged entries */}
                    {(() => {
                        const docs = detail.effectiveDocuments || detail.documents || [];
                        if (docs.length === 0) return null;

                        const DOC_TYPE_LABELS = {
                            REGISTRATION: 'Proof of Organization (Registration)',
                            PROOF_OF_AUTHORITY: 'Proof of Authority',
                            SIGNATORY_ID: 'Signatory Identification (Optional)',
                            ADDITIONAL: 'Additional Document',
                        };
                        const DOC_TYPE_ORDER = ['REGISTRATION', 'PROOF_OF_AUTHORITY', 'SIGNATORY_ID', 'ADDITIONAL'];

                        // Find the latest info request timestamp to flag post-request docs
                        const lastRequestAt = detail.reviewHistory
                            ? (() => {
                                const reqs = detail.reviewHistory.filter(h => h.action === 'REQUEST_INFO');
                                if (reqs.length === 0) return null;
                                return new Date(reqs[reqs.length - 1].timestamp);
                            })()
                            : (detail.additionalDocsRequestedAt ? new Date(detail.additionalDocsRequestedAt) : null);

                        // Group by docType
                        const grouped = {};
                        DOC_TYPE_ORDER.forEach(t => { grouped[t] = []; });
                        docs.forEach(doc => {
                            const key = doc.docType || 'ADDITIONAL';
                            if (!grouped[key]) grouped[key] = [];
                            grouped[key].push(doc);
                        });

                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '12px 0' }}>
                                {DOC_TYPE_ORDER.map(docType => {
                                    const items = grouped[docType];
                                    if (!items || items.length === 0) return null;
                                    return (
                                        <div key={docType} style={{ padding: '0 16px' }}>
                                            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#566071', marginBottom: 8 }}>
                                                {DOC_TYPE_LABELS[docType] || docType}
                                                {['REGISTRATION', 'PROOF_OF_AUTHORITY'].includes(docType) && (
                                                    <span style={{ marginLeft: 6, fontSize: 10, color: '#b3145a', fontWeight: 700 }}>MANDATORY</span>
                                                )}
                                                {docType === 'SIGNATORY_ID' && (
                                                    <span style={{ marginLeft: 6, fontSize: 10, color: '#566071', fontWeight: 600 }}>OPTIONAL</span>
                                                )}
                                            </div>
                                            {items.map((doc, idx) => {
                                                const isSuperseded = !!doc.supersededAt;
                                                const isPostRequest = lastRequestAt && doc.uploadedAt && new Date(doc.uploadedAt) > lastRequestAt;
                                                return (
                                                    <div
                                                        key={doc._id || idx}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 12,
                                                            padding: '8px 12px',
                                                            borderRadius: 8,
                                                            marginBottom: 4,
                                                            background: isSuperseded ? '#f2f4f8' : (isPostRequest ? '#f0eaff' : '#fff'),
                                                            border: isPostRequest ? '1px solid #a855f7' : '1px solid #eef1f5',
                                                            opacity: isSuperseded ? 0.6 : 1,
                                                        }}
                                                    >
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#14161f' }}>
                                                                {doc.label || DOC_TYPE_LABELS[doc.docType] || doc.docType}
                                                                {isPostRequest && (
                                                                    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#4e299c', background: '#f0eaff', padding: '2px 6px', borderRadius: 999 }}>
                                                                        NEW — Post-request
                                                                    </span>
                                                                )}
                                                                {isSuperseded && (
                                                                    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#9a3d13', background: '#fff0e7', padding: '2px 6px', borderRadius: 999 }}>
                                                                        Superseded
                                                                    </span>
                                                                )}
                                                                {doc._legacy && (
                                                                    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: '#566071' }}>
                                                                        (legacy)
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: '#7f8999', marginTop: 2 }}>
                                                                Uploaded: {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : '—'}
                                                                {isSuperseded && ` · Superseded: ${new Date(doc.supersededAt).toLocaleString()}`}
                                                            </div>
                                                        </div>
                                                        {doc.url && (
                                                            <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                View Document
                                                            </a>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </Surface>

                {/* ── Review History Timeline ── */}
                <Surface title="Review History">
                    {(() => {
                        const history = detail.reviewHistory || [];
                        const legacyNote = detail.applicationStatusNote;
                        const hasHistory = history.length > 0;
                        const hasLegacy = !hasHistory && legacyNote;

                        if (!hasHistory && !hasLegacy) {
                            return <div style={{ padding: '16px', color: '#566071', fontSize: 13, fontStyle: 'italic' }}>No review history recorded yet.</div>;
                        }

                        const ACTION_LABELS = {
                            REQUEST_INFO: 'Information Requested',
                            APPROVE: 'Approved',
                            REJECT: 'Rejected',
                            RESUBMIT: 'Resubmitted by Applicant',
                        };
                        const ACTION_COLORS = {
                            REQUEST_INFO: '#9a3d13',
                            APPROVE: '#096844',
                            REJECT: '#b3145a',
                            RESUBMIT: '#153e9d',
                        };

                        const entries = hasHistory ? history : [{
                            action: 'REQUEST_INFO',
                            note: legacyNote,
                            adminName: null,
                            timestamp: detail.reviewedAt || null,
                            resultingStatus: detail.applicationStatus,
                            _isLegacy: true,
                        }];

                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, padding: '8px 16px' }}>
                                {entries.map((entry, idx) => {
                                    const color = ACTION_COLORS[entry.action] || '#566071';
                                    return (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            gap: 12,
                                            padding: '12px 0',
                                            borderBottom: idx < entries.length - 1 ? '1px solid #eef1f5' : 'none',
                                        }}>
                                            <div style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: 999,
                                                background: color,
                                                marginTop: 5,
                                                flexShrink: 0,
                                            }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color }}>
                                                    {ACTION_LABELS[entry.action] || entry.action}
                                                    {entry._isLegacy && <span style={{ fontWeight: 400, color: '#7f8999', marginLeft: 8, fontSize: 11 }}>(legacy record)</span>}
                                                </div>
                                                {entry.note && (
                                                    <div style={{ fontSize: 13, color: '#14161f', marginTop: 4 }}>
                                                        "{entry.note}"
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 11, color: '#7f8999', marginTop: 4 }}>
                                                    {entry.adminName ? `By ${entry.adminName}` : (entry.adminId ? `By admin ${entry.adminId}` : 'By applicant')}
                                                    {entry.timestamp ? ` · ${new Date(entry.timestamp).toLocaleString()}` : ''}
                                                    {entry.resultingStatus ? ` · Status → ${APPLICATION_STATUS_LABELS[entry.resultingStatus] || entry.resultingStatus}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </Surface>

                {/* ── Finding 5: Application Review Actions in detail view ── */}
                {isAppPending && (
                    <Surface title="Application Review Actions">
                        <DetailApplicationActions
                            communityId={detail._id}
                            onApprove={() => doApplicationAction(detail._id, 'approve')}
                            onReject={(note) => doApplicationAction(detail._id, 'reject', { note })}
                            onRequestInfo={(note) => doApplicationAction(detail._id, 'request-info', { note })}
                            actionLoading={actionLoading}
                        />
                    </Surface>
                )}

                {/* ── Deactivation History ── */}
                {(detail.status === 'DEACTIVATED' || detail.deactivatedAt) && (
                    <Surface title="Deactivation History">
                        <DetailGrid>
                            <DetailItem label="Status">
                                <StatusPill status="deactivated">Deactivated</StatusPill>
                            </DetailItem>
                            <DetailItem label="Deactivated At" value={detail.deactivatedAt ? new Date(detail.deactivatedAt).toLocaleString() : '—'} />
                            <DetailItem label="Deactivated By" value={detail.deactivatedBy || '—'} />
                            <DetailItem label="Reason" value={detail.deactivatedReason || 'No reason recorded'} wide />
                            {detail.reactivationRequestedAt && (
                                <>
                                    <DetailItem label="Reactivation Requested At" value={new Date(detail.reactivationRequestedAt).toLocaleString()} />
                                    <DetailItem label="Requested By" value={detail.reactivationRequestedBy || '—'} />
                                </>
                            )}
                        </DetailGrid>
                    </Surface>
                )}

                {/* ── Reactivation Request Banner ── */}
                {detail.status === 'DEACTIVATED' && detail.reactivationRequestedAt && (
                    <Surface title="Pending Reactivation Request">
                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ fontSize: 14, color: '#14161f' }}>
                                The community owner has requested reactivation. Review the deactivation history above before confirming.
                            </div>
                            <div style={{ fontSize: 13, color: '#566071' }}>
                                <strong>Requested:</strong> {new Date(detail.reactivationRequestedAt).toLocaleString()}
                            </div>
                            <ActionButton
                                variant="primary"
                                disabled={actionLoading}
                                onClick={() => doModerationAction(detail._id, 'reactivate')}
                            >
                                Confirm Reactivation
                            </ActionButton>
                        </div>
                    </Surface>
                )}

                {/* ── Moderation Actions ── */}
                <Surface title="Moderation Actions">
                    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {detail.status === 'ACTIVE' && (
                            <>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: 200 }}>
                                        <label style={{ display: 'block', fontSize: 12, color: '#566071', marginBottom: 4 }}>Reason for suspension</label>
                                        <input
                                            type="text"
                                            value={actionNote}
                                            onChange={e => setActionNote(e.target.value)}
                                            placeholder="Enter reason…"
                                            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e7eaf0', fontSize: 14 }}
                                        />
                                    </div>
                                    <ActionButton
                                        variant="primary"
                                        disabled={actionLoading || !actionNote.trim()}
                                        onClick={() => doModerationAction(detail._id, 'suspend', { reason: actionNote })}
                                    >
                                        Suspend Community
                                    </ActionButton>
                                </div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
                                    <div style={{ flex: 1, minWidth: 200 }}>
                                        <label style={{ display: 'block', fontSize: 12, color: '#566071', marginBottom: 4 }}>Reason for deactivation</label>
                                        <input
                                            type="text"
                                            value={deactivateReason}
                                            onChange={e => setDeactivateReason(e.target.value)}
                                            placeholder="Enter reason…"
                                            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e7eaf0', fontSize: 14 }}
                                        />
                                    </div>
                                    <ActionButton
                                        variant="secondary"
                                        disabled={actionLoading || !deactivateReason.trim()}
                                        onClick={() => doModerationAction(detail._id, 'deactivate', { reason: deactivateReason })}
                                    >
                                        Deactivate Community
                                    </ActionButton>
                                </div>
                            </>
                        )}
                        {detail.status === 'SUSPENDED' && (
                            <ActionButton
                                variant="primary"
                                disabled={actionLoading}
                                onClick={() => doModerationAction(detail._id, 'reactivate')}
                            >
                                Reactivate Community
                            </ActionButton>
                        )}
                        {detail.status === 'DEACTIVATED' && !detail.reactivationRequestedAt && (
                            <ActionButton
                                variant="primary"
                                disabled={actionLoading}
                                onClick={() => doModerationAction(detail._id, 'reactivate')}
                            >
                                Reactivate Community
                            </ActionButton>
                        )}
                        {detail.status === 'SUSPENDED' && detail.suspendedReason && (
                            <div style={{ fontSize: 13, color: '#566071' }}>
                                <strong>Suspension reason:</strong> {detail.suspendedReason}
                            </div>
                        )}
                    </div>
                </Surface>
            </PageShell>
        );
    }

    /* ── Main views (List / Applications / Reactivation Requests) ──── */
    return (
        <PageShell>
            <PageHeader
                eyebrow="Communities"
                title="Community Management"
                description="Review applications, manage active communities, and moderate content."
            />

            {/* ── View toggle ── */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e7eaf0', marginBottom: 16 }}>
                <button style={TAB_STYLE(view === VIEWS.LIST)} onClick={() => setView(VIEWS.LIST)}>
                    All Communities ({listCounts.ALL})
                </button>
                <button style={TAB_STYLE(view === VIEWS.APPLICATIONS)} onClick={() => setView(VIEWS.APPLICATIONS)}>
                    Applications ({appCounts.ALL})
                </button>
                <button style={TAB_STYLE(view === VIEWS.REACTIVATION)} onClick={() => setView(VIEWS.REACTIVATION)}>
                    Reactivation Requests ({reactivationRequests.length})
                </button>
            </div>

            {/* ── Search ── */}
            <div style={{ marginBottom: 16 }}>
                <Search value={search} onChange={e => setSearch(e.target.value)} placeholder="Search communities…" />
            </div>

            {/* ═══════════ LIST VIEW ═══════════ */}
            {view === VIEWS.LIST && (
                <>
                    {/* Status tabs */}
                    <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e7eaf0', marginBottom: 16 }}>
                        {LIST_TABS.map(t => (
                            <button key={t} style={TAB_STYLE(listTab === t)} onClick={() => setListTab(t)}>
                                {t === 'ALL' ? 'All' : LIFECYCLE_STATUS_LABELS[t] || t} ({listCounts[t] || 0})
                            </button>
                        ))}
                    </div>

                    {/* Metrics */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                        <MetricCard tone="green" label="Active" value={listCounts.ACTIVE || 0} />
                        <MetricCard tone="orange" label="Suspended" value={listCounts.SUSPENDED || 0} />
                        <MetricCard tone="purple" label="Deactivated" value={listCounts.DEACTIVATED || 0} />
                    </div>

                    <Surface title={`Communities (${filteredCommunities.length})`}>
                        {loading && <StatePanel state="loading">Loading communities…</StatePanel>}
                        {!loading && filteredCommunities.length === 0 && <Empty>No communities found.</Empty>}
                        {filteredCommunities.map(c => (
                            <Row
                                key={c._id}
                                right={
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        {/* Finding 4: Only show lifecycle pill after approval */}
                                        {c.applicationStatus === 'APPROVED' && (
                                            <StatusPill status={(c.status || '').toLowerCase()}>
                                                {pillLabel(c.status)}
                                            </StatusPill>
                                        )}
                                        <StatusPill status={(c.applicationStatus || '').toLowerCase()}>
                                            {pillLabel(c.applicationStatus)}
                                        </StatusPill>
                                        {c.reactivationRequestedAt && (
                                            <StatusPill status="pending">Reactivation Requested</StatusPill>
                                        )}
                                    </div>
                                }
                            >
                                <RowText>
                                    <RowTitle>
                                        <span
                                            style={{ color: '#7c3aed', cursor: 'pointer' }}
                                            onClick={() => openDetail(c)}
                                        >
                                            {c.name}
                                        </span>
                                    </RowTitle>
                                    <RowMeta>
                                        {c.organizationType} · {c.city}, {c.country} · Admin: {c.orgAdmin?.name || c.orgAdmin?.firstName || '—'} · Members: {c.activeMemberCount ?? 0} · Providers: {c.activeProviderCount ?? 0}
                                    </RowMeta>
                                </RowText>
                            </Row>
                        ))}
                    </Surface>
                </>
            )}

            {/* ═══════════ APPLICATIONS VIEW ═══════════ */}
            {view === VIEWS.APPLICATIONS && (
                <>
                    {/* Status tabs */}
                    <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e7eaf0', marginBottom: 16 }}>
                        {APP_TABS.map(t => (
                            <button key={t} style={TAB_STYLE(appTab === t)} onClick={() => setAppTab(t)}>
                                {t === 'ALL' ? 'All' : APPLICATION_STATUS_LABELS[t] || t} ({appCounts[t] || 0})
                            </button>
                        ))}
                    </div>

                    <Surface title={`Applications (${filteredApplications.length})`}>
                        {loading && <StatePanel state="loading">Loading applications…</StatePanel>}
                        {!loading && filteredApplications.length === 0 && <Empty>No applications found.</Empty>}
                        {filteredApplications.map(c => (
                            <ApplicationRow
                                key={c._id}
                                community={c}
                                onApprove={() => doApplicationAction(c._id, 'approve')}
                                onReject={(note) => doApplicationAction(c._id, 'reject', { note })}
                                onRequestInfo={(note) => doApplicationAction(c._id, 'request-info', { note })}
                                onViewDetail={() => openDetail(c)}
                                actionLoading={actionLoading}
                            />
                        ))}
                    </Surface>
                </>
            )}

            {/* ═══════════ REACTIVATION REQUESTS VIEW ═══════════ */}
            {view === VIEWS.REACTIVATION && (
                <Surface title={`Reactivation Requests (${reactivationRequests.length})`}>
                    {loading && <StatePanel state="loading">Loading reactivation requests…</StatePanel>}
                    {!loading && reactivationRequests.length === 0 && <Empty>No pending reactivation requests.</Empty>}
                    {reactivationRequests.map(c => (
                        <Row
                            key={c._id}
                            right={
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <StatusPill status="pending">Reactivation Requested</StatusPill>
                                </div>
                            }
                        >
                            <RowText>
                                <RowTitle>
                                    <span
                                        style={{ color: '#7c3aed', cursor: 'pointer' }}
                                        onClick={() => openDetail(c)}
                                    >
                                        {c.name}
                                    </span>
                                </RowTitle>
                                <RowMeta>
                                    {c.organizationType} · {c.city}, {c.country} · Admin: {c.orgAdmin?.name || c.orgAdmin?.firstName || '—'}
                                </RowMeta>
                                <RowMeta dim>
                                    Deactivated: {c.deactivatedAt ? new Date(c.deactivatedAt).toLocaleString() : '—'}
                                    {c.deactivatedReason ? ` · Reason: ${c.deactivatedReason}` : ''}
                                    {' · '}Requested: {c.reactivationRequestedAt ? new Date(c.reactivationRequestedAt).toLocaleString() : '—'}
                                </RowMeta>
                                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <ActionButton
                                        variant="primary"
                                        disabled={actionLoading}
                                        onClick={() => openDetail(c)}
                                    >
                                        Review &amp; Confirm
                                    </ActionButton>
                                </div>
                            </RowText>
                        </Row>
                    ))}
                </Surface>
            )}
        </PageShell>
    );
}

/* ─── Finding 5: Detail-view application review actions ───────────────── */
function DetailApplicationActions({ communityId, onApprove, onReject, onRequestInfo, actionLoading }) {
    const [expanded, setExpanded] = useState(false);
    const [note, setNote] = useState('');

    return (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <ActionButton
                    variant="primary"
                    disabled={actionLoading}
                    onClick={onApprove}
                >
                    Approve
                </ActionButton>
                <ActionButton
                    variant="secondary"
                    disabled={actionLoading}
                    onClick={() => setExpanded(expanded === 'reject' ? false : 'reject')}
                >
                    Reject
                </ActionButton>
                <ActionButton
                    variant="secondary"
                    disabled={actionLoading}
                    onClick={() => setExpanded(expanded === 'info' ? false : 'info')}
                >
                    Request Info
                </ActionButton>
            </div>

            {expanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input
                        type="text"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder={expanded === 'reject' ? 'Rejection reason (optional)…' : 'What info is needed…'}
                        style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e7eaf0', fontSize: 14 }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                        <ActionButton
                            variant="primary"
                            disabled={actionLoading || (expanded === 'info' && !note.trim())}
                            onClick={() => {
                                if (expanded === 'reject') onReject(note);
                                else onRequestInfo(note);
                                setNote('');
                                setExpanded(false);
                            }}
                        >
                            {expanded === 'reject' ? 'Confirm Reject' : 'Send Info Request'}
                        </ActionButton>
                        <ActionButton variant="secondary" onClick={() => { setExpanded(false); setNote(''); }}>
                            Cancel
                        </ActionButton>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Application Row with inline actions ─────────────────────────────── */
function ApplicationRow({ community: c, onApprove, onReject, onRequestInfo, onViewDetail, actionLoading }) {
    const [expanded, setExpanded] = useState(false);
    const [note, setNote] = useState('');

    const isPending = c.applicationStatus === 'PENDING_REVIEW';
    const isMoreInfo = c.applicationStatus === 'MORE_INFO_REQUIRED';

    return (
        <Row
            right={
                <StatusPill status={(c.applicationStatus || '').toLowerCase()}>
                    {pillLabel(c.applicationStatus)}
                </StatusPill>
            }
        >
            <RowText>
                <RowTitle>
                    <span
                        style={{ color: '#7c3aed', cursor: 'pointer' }}
                        onClick={onViewDetail}
                    >
                        {c.name}
                    </span>
                </RowTitle>
                <RowMeta>
                    {c.organizationType} · {c.city}, {c.country} · Admin: {c.orgAdmin?.name || c.orgAdmin?.firstName || '—'} · Submitted: {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                </RowMeta>

                {/* Action buttons */}
                {(isPending || isMoreInfo) && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <ActionButton
                            variant="primary"
                            disabled={actionLoading}
                            onClick={onApprove}
                        >
                            Approve
                        </ActionButton>
                        <ActionButton
                            variant="secondary"
                            disabled={actionLoading}
                            onClick={() => setExpanded(expanded === 'reject' ? false : 'reject')}
                        >
                            Reject
                        </ActionButton>
                        <ActionButton
                            variant="secondary"
                            disabled={actionLoading}
                            onClick={() => setExpanded(expanded === 'info' ? false : 'info')}
                        >
                            Request Info
                        </ActionButton>
                    </div>
                )}

                {/* Expanded note input for reject/request-info */}
                {expanded && (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <input
                            type="text"
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder={expanded === 'reject' ? 'Rejection reason (optional)…' : 'What info is needed…'}
                            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e7eaf0', fontSize: 14 }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <ActionButton
                                variant="primary"
                                disabled={actionLoading || (expanded === 'info' && !note.trim())}
                                onClick={() => {
                                    if (expanded === 'reject') onReject(note);
                                    else onRequestInfo(note);
                                    setNote('');
                                    setExpanded(false);
                                }}
                            >
                                {expanded === 'reject' ? 'Confirm Reject' : 'Send Info Request'}
                            </ActionButton>
                            <ActionButton variant="secondary" onClick={() => { setExpanded(false); setNote(''); }}>
                                Cancel
                            </ActionButton>
                        </div>
                    </div>
                )}

                {/* Show application note if exists */}
                {c.applicationStatusNote && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#9a3d13', fontStyle: 'italic' }}>
                        Admin note: {c.applicationStatusNote}
                    </div>
                )}
            </RowText>
        </Row>
    );
}
